import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle2, Loader2, MapPin, CalendarDays, Users, ShieldCheck, CreditCard } from "lucide-react";
import { api, fmtDateTime, euro, apiError } from "@/lib/api";
import { toast } from "sonner";

const PaymentPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    api.get(`/pay/${token}`).then((r) => {
      setData(r.data);
      if (r.data.payment_status === "paid") setPaid(true);
    }).catch(() => setError("Dieser Zahlungslink ist ungültig oder abgelaufen."));
  }, [token]);

  const payStripe = async () => {
    if (!terms) { toast.error("Bitte AGB & Stornobedingungen bestätigen"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/pay/${token}/stripe`, { terms_accepted: true, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  if (error) return <Shell><div className="text-center py-16" data-testid="pay-error"><p className="text-[#ff0033] font-heading text-2xl uppercase">Link ungültig</p><p className="text-neutral-400 mt-3">{error}</p></div></Shell>;
  if (!data) return <Shell><div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#ff0033]" size={32} /></div></Shell>;

  if (paid) return (
    <Shell>
      <div className="text-center py-14" data-testid="pay-success">
        <CheckCircle2 className="mx-auto text-green-500 mb-4" size={56} />
        <p className="font-heading text-3xl uppercase text-white">Zahlung erfolgreich</p>
        <p className="text-neutral-300 mt-3">Vielen Dank, {data.customer_name}. Ihre Buchung für <span className="text-white">{data.course_name}</span> ist verbindlich bestätigt.</p>
        <p className="text-neutral-500 text-sm mt-2">Bestätigung & Rechnung erhalten Sie per E-Mail. Buchungsnr. {data.booking_number}</p>
      </div>
    </Shell>
  );

  const dates = data.dates && data.dates.length ? data.dates : (data.start ? [data.start] : []);

  return (
    <Shell>
      <p className="font-mono-lab text-[11px] tracking-[0.25em] text-[#ff0033] uppercase mb-2">// Zahlungsaufforderung</p>
      <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-white mb-1">Buchung abschließen</h1>
      <p className="text-neutral-400 text-sm mb-6">Buchungsnr. {data.booking_number} · {data.customer_name}</p>

      <div className="bg-[#0F0F0F] border border-white/10 p-5 mb-5" data-testid="pay-summary">
        <p className="font-heading text-xl text-white mb-3">{data.course_name}</p>
        <div className="space-y-2 text-sm">
          {dates.map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-neutral-300"><CalendarDays size={15} className="text-[#ff0033]" /> {fmtDateTime(d, "de")}</div>
          ))}
          {data.location && <div className="flex items-center gap-2 text-neutral-300"><MapPin size={15} className="text-[#ff0033]" /> {data.location}</div>}
          <div className="flex items-center gap-2 text-neutral-300"><Users size={15} className="text-[#ff0033]" /> {data.persons} {data.persons === 1 ? "Person" : "Personen"}</div>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 mt-4 pt-4">
          <span className="text-neutral-400 font-mono-lab text-xs uppercase tracking-widest">Gesamtbetrag</span>
          <span className="font-heading text-3xl text-white" data-testid="pay-amount">{euro(data.amount)}</span>
        </div>
      </div>

      <div className="border border-[#ff0033]/30 bg-[#ff0033]/5 p-4 mb-5 text-sm text-neutral-300">
        <p className="flex items-start gap-2"><ShieldCheck size={16} className="text-[#ff0033] mt-0.5 shrink-0" /> Ihre Buchung gilt nach erfolgreichem Zahlungseingang als verbindlich bestätigt. Eine kostenfreie Stornierung ist bis <strong className="text-white">48&nbsp;Stunden</strong> vor Kursbeginn möglich – der bezahlte Betrag wird dann automatisch über die verwendete Zahlungsart erstattet.</p>
      </div>

      <label className="flex items-start gap-3 text-sm text-neutral-300 mb-5 cursor-pointer">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="pay-terms" className="accent-[#ff0033] mt-1 w-4 h-4" />
        <span>Ich akzeptiere die <a href="/impressum" target="_blank" className="text-[#ff0033] underline">AGB & Stornobedingungen</a> sowie die <a href="/impressum" target="_blank" className="text-[#ff0033] underline">Datenschutzerklärung</a>.</span>
      </label>

      <div className={`space-y-3 transition-opacity ${terms ? "" : "opacity-40 pointer-events-none"}`} data-testid="pay-methods">
        <button onClick={payStripe} disabled={busy} data-testid="pay-stripe-btn" className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-4 hover:bg-[#cc0029] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} Mit Karte bezahlen (Stripe)
        </button>

        {data.paypal_enabled && data.paypal_client_id && (
          <div data-testid="pay-paypal">
            <PayPalScriptProvider options={{ clientId: data.paypal_client_id, currency: "EUR", components: "buttons", intent: "capture" }}>
              <PayPalButtons
                forceReRender={[terms, data.amount]}
                disabled={!terms}
                style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                createOrder={async () => {
                  const { data: res } = await api.post(`/pay/${token}/paypal/create`, { terms_accepted: true });
                  return res.order_id;
                }}
                onApprove={async () => {
                  try {
                    const { data: res } = await api.post(`/pay/${token}/paypal/capture`);
                    if (res.payment_status === "paid") { setPaid(true); toast.success("Zahlung erfolgreich"); }
                    else toast.error("Zahlung konnte nicht bestätigt werden. Bitte kontaktieren Sie uns.");
                  } catch (e) { toast.error(apiError(e)); }
                }}
                onError={() => toast.error("PayPal-Fehler. Bitte erneut versuchen oder Karte nutzen.")}
              />
            </PayPalScriptProvider>
          </div>
        )}
      </div>
    </Shell>
  );
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-4 py-10">
    <div className="w-full max-w-lg">
      <div className="mb-8">
        <p className="font-heading text-xl uppercase tracking-tight text-white leading-none">Golfcoaching Academy</p>
        <p className="font-mono-lab text-[10px] tracking-[0.3em] text-[#ff0033] uppercase mt-1">Wendlingen · by golfcoaching.online</p>
      </div>
      {children}
      <p className="text-center text-neutral-600 text-xs mt-10">Sichere Zahlung · Michael Wolf Golfprofessional · training@wolfmichael.de</p>
    </div>
  </div>
);

export default PaymentPage;
